// The web's motion (D159, the Motion page): each movement names its duration and easing token, and nothing here holds a
// number of its own — the values are orca.css's. Reduced motion, the system's or the person's own (A7U5,
// `data-reduced-motion`), makes every movement 0 ms: nothing slides, pops, opens or glows. motion.css draws the
// movements; `navigationMotion` picks the route's for the router's view transition: from 840 px a fade of the page body
// alone (a desktop app does not slide, E22U5), below it push, pop or a tab switch.
import { useSyncExternalStore } from 'react'

export const motions = {
  push: { duration: 'push', easing: 'standard' },
  pop: { duration: 'push', easing: 'standard' },
  tab: { duration: 'tab', easing: 'tab' },
  // From 840 px the page body fades for at most 120 ms: the hover token's length; the chrome and the banners stay.
  fade: { duration: 'hover', easing: 'fade' },
  sheet: { duration: 'sheet', easing: 'sheet' },
  scrim: { duration: 'scrim', easing: 'fade' },
  insert: { duration: 'insert', easing: 'standard' },
  remove: { duration: 'remove', easing: 'standard' },
  done: { duration: 'done', easing: 'standard' },
  glow: { duration: 'glow', easing: 'fade' },
} as const

export type Motion = keyof typeof motions

/** The CSS duration and easing of a movement: the tokens, or 0 ms under reduced motion. */
export function timing(motion: Motion, reduced: boolean) {
  const { duration, easing } = motions[motion]
  return { duration: reduced ? '0ms' : `var(--duration-${duration})`, easing: `var(--ease-${easing})` }
}

/** The movement's length in milliseconds, read from the token on the page; 0 under reduced motion. */
export function durationMs(motion: Motion, reduced: boolean, root: Element = document.documentElement) {
  if (reduced) return 0
  const value = getComputedStyle(root).getPropertyValue(`--duration-${motions[motion].duration}`).trim()
  const ms = Number.parseFloat(value)
  if (Number.isNaN(ms)) return 0
  return value.endsWith('ms') ? ms : ms * 1000
}

const query = '(prefers-reduced-motion: reduce)'

export function isReducedMotion() {
  if (document.documentElement.dataset.reducedMotion === 'true') return true
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches
}

function subscribe(onChange: () => void) {
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(query) : undefined
  media?.addEventListener('change', onChange)
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-reduced-motion'] })
  return () => {
    media?.removeEventListener('change', onChange)
    observer.disconnect()
  }
}

/** Reduced motion, kept current when the system's setting or the person's choice changes. */
export function useReducedMotion() {
  return useSyncExternalStore(subscribe, isReducedMotion, () => false)
}

const depth = (path: string) => path.split('/').filter(Boolean).length

/** From this window width the web reads as a desktop app: the `expanded` class (`--breakpoint-expanded`, D185). */
export const EXPANDED_WIDTH = 840

export type NavigationMotion = 'push' | 'pop' | 'tab' | 'fade'

/**
 * The route's movement. From 840 px every route fades the page body; below it, deeper under the same path pushes, back
 * up pops, anything else is a tab switch. The first page, a change of search alone and reduced motion do not move.
 */
export function navigationMotion(
  from: string | undefined,
  to: string,
  reduced: boolean,
  width: number = window.innerWidth,
): NavigationMotion | false {
  if (reduced || from === undefined || from === to) return false
  if (width >= EXPANDED_WIDTH) return 'fade'
  if (to.startsWith(`${from}/`) && depth(to) > depth(from)) return 'push'
  if (from.startsWith(`${to}/`) && depth(from) > depth(to)) return 'pop'
  return 'tab'
}
